/**
 * 主题详情弹窗（旧 views/themes/components/ThemeDetailModal.vue）
 */
import { Badge, Card, Descriptions, Empty, Modal, Space, Tag, Typography } from 'antd'
import { JsonViewer } from '@/components/json-viewer/JsonViewer'
import { MarkdownView } from '@/components/MarkdownView'
import { resolveThemeAssetUrl } from '@/api/services/themes'
import { categoryColor, categoryLabel, formatThemeDate } from './constants'
import { useThemeStats } from './hooks'
import { ThemeUsageStats } from './ThemeUsageStats'
import { themeTags, type Theme } from './types'
import styles from './ThemeDetailModal.module.css'

const NOT_SET = <Typography.Text type="secondary">未设置</Typography.Text>

function ThemeDetailContent({ theme }: Readonly<{ theme: Theme }>) {
  const { data: stats } = useThemeStats(theme.id)
  const tags = themeTags(theme)
  const cards = Array.isArray(theme.character_cards) ? theme.character_cards : []
  const hasStyleHints = theme.style_hints && Object.keys(theme.style_hints).length > 0

  return (
    <div className={styles.detail}>
      <Descriptions title="基本信息" column={{ xs: 1, sm: 2 }} bordered>
        <Descriptions.Item label="主题代码">
          <Tag color="blue">{theme.code}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="主题标题">
          <MarkdownView content={theme.title} inline />
        </Descriptions.Item>
        <Descriptions.Item label="分类">
          <Tag color={categoryColor(theme.category)}>{categoryLabel(theme.category)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="CEFR 等级">
          <Tag color="blue">{theme.cefr_level}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="语言">
          <Tag color="blue">{theme.locale}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="状态">
          <Tag color={theme.is_active ? 'success' : 'default'}>{theme.is_active ? '启用' : '禁用'}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="版本">
          <Badge count={theme.version} color="green" />
        </Descriptions.Item>
        <Descriptions.Item label="创建时间">{formatThemeDate(theme.created_at)}</Descriptions.Item>
        <Descriptions.Item label="更新时间">{formatThemeDate(theme.updated_at)}</Descriptions.Item>
        <Descriptions.Item label="标签">
          {tags.length ? (
            <Space wrap>
              {tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </Space>
          ) : (
            NOT_SET
          )}
        </Descriptions.Item>
      </Descriptions>

      {theme.description && (
        <Card title="主题描述" className={styles.section}>
          <MarkdownView content={theme.description} />
        </Card>
      )}

      <Card title="展示资产" className={styles.section}>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="封面">
            {theme.cover_image_url ? (
              <img className={styles.cover} src={resolveThemeAssetUrl(theme.cover_image_url)} alt="主题封面" />
            ) : (
              NOT_SET
            )}
          </Descriptions.Item>
          <Descriptions.Item label="列表钩子">{theme.list_hook || '未设置'}</Descriptions.Item>
        </Descriptions>
        <div className={styles.cardsTitle}>角色卡</div>
        {cards.length ? (
          <div className={styles.cardsRow}>
            {cards.map((card, index) => (
              <div key={`${card.name || 'card'}-${index}`} className={styles.characterCard}>
                {card.avatar_url ? (
                  <img src={resolveThemeAssetUrl(card.avatar_url)} alt={card.name || '角色头像'} />
                ) : (
                  <div className={styles.avatarPlaceholder}>无头像</div>
                )}
                <div className={styles.characterName}>{card.name || '未填写名字'}</div>
                <div className={styles.characterRole}>{card.role || '未填写身份'}</div>
              </div>
            ))}
          </div>
        ) : (
          <Empty description="暂无角色卡" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <Card title="初始固定设定" className={styles.section}>
        <JsonViewer data={theme.initial_fixed_setting} />
      </Card>

      {theme.initial_recap && (
        <Card title="初始回顾" className={styles.section}>
          <p>{theme.initial_recap}</p>
        </Card>
      )}

      <Card title="风格提示 (Style Hints)" className={styles.section}>
        {hasStyleHints ? (
          <JsonViewer data={theme.style_hints} />
        ) : (
          <Empty description="暂无风格提示" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <Card title="主题专属 Prompt 提示词 (Theme Specific Prompt)" className={styles.section}>
        {theme.theme_specific_prompt ? (
          <>
            <Typography.Paragraph ellipsis={{ rows: 10, expandable: true, symbol: '展开全部' }} className={styles.prompt}>
              {theme.theme_specific_prompt}
            </Typography.Paragraph>
            <div className={styles.promptNote}>
              <div>
                <strong>💡 说明：</strong>此提示词将在 AI 生成篇章时作为额外指导，支持 Markdown 格式
              </div>
              <div className={styles.promptCount}>
                <strong>📝 字数：</strong>
                {theme.theme_specific_prompt.length} 字符
              </div>
            </div>
          </>
        ) : (
          <Empty description="暂无主题专属 Prompt" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      <Card title="第一篇专属内容 (First Episode)" className={styles.section}>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="第一篇标题 (First Episode Title)">
            {theme.first_episode_title ? <MarkdownView content={theme.first_episode_title} inline /> : '（未设置）'}
          </Descriptions.Item>
          <Descriptions.Item label="第一篇预告内容 (First Episode Story Text)">
            {theme.first_episode_story_text ? (
              <MarkdownView content={theme.first_episode_story_text} />
            ) : (
              <Typography.Text type="secondary">（未设置）</Typography.Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="使用统计" className={styles.section}>
        <ThemeUsageStats stats={stats} />
      </Card>
    </div>
  )
}

export function ThemeDetailModal({ theme, onClose }: Readonly<{ theme: Theme | null; onClose: () => void }>) {
  return (
    <Modal title="主题详情" open={theme !== null} width={900} footer={null} destroyOnHidden onCancel={onClose}>
      {theme && <ThemeDetailContent theme={theme} />}
    </Modal>
  )
}
